import { Component, type ErrorInfo, type ReactNode } from 'react';

import styles from './error-boundary.module.css';

export interface ErrorBoundaryProps {
  readonly children: ReactNode;
  readonly fallbackTitle?: string;
}

interface State {
  readonly hasError: boolean;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, State> {
  public override state: State = { hasError: false };

  public static getDerivedStateFromError(_error: unknown): State {
    return { hasError: true };
  }

  public override componentDidCatch(error: Error, info: ErrorInfo): void {
    /* Structured reporting lands with the API substrate; swallow to keep UI up. */
    void error;
    void info;
  }

  private readonly handleRetry = (): void => {
    this.setState({ hasError: false });
  };

  public override render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div className={styles['root']} role="alert">
          <div className={styles['title']}>
            {this.props.fallbackTitle ?? 'Something went wrong'}
          </div>
          <div className={styles['message']}>Please try again.</div>
          <button type="button" className={styles['retry']} onClick={this.handleRetry}>
            Try again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
